// Enhanced Cable Asset Data with descriptions, relationships, and metadata
// Used for Connected Highlighting and Details Panel features
// Cables for Switchgear, Transformer, and Motor connections

export const CABLE_DATA = [
  {
    id: "1",
    number: 1,
    name: "Cable Insulation",
    color: "#8B5CF6",
    failureModes: [
      {
        name: "Partial Discharge",
        description: "Localized electrical discharges within voids or defects in cable insulation that do not completely bridge the insulation gap.",
        importance: "Progressive PD activity erodes insulation material over time, eventually leading to complete breakdown and cable failure.",
        approach: "Perform online or offline PD testing to locate and assess severity. Trend PD levels over time to predict remaining life.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test", "VLF Tan Delta Test"],
        relatedMonitoring: ["Online PD Monitoring", "Distributed Temperature Sensing"],
        images: []
      },
      {
        name: "Water Treeing",
        description: "Tree-like micro-channels that form in XLPE insulation due to moisture ingress combined with electrical stress over time.",
        importance: "Water trees reduce dielectric strength and can convert to electrical trees, leading to rapid failure. Common in aged XLPE cables.",
        approach: "Perform VLF Tan Delta testing to detect water tree degradation. Consider replacement planning for severely affected cables.",
        severity: "Critical",
        relatedTests: ["VLF Tan Delta Test", "Dielectric Spectroscopy"],
        relatedMonitoring: ["Distributed Temperature Sensing"],
        images: []
      },
      {
        name: "Thermal Degradation",
        description: "Breakdown of insulation properties due to sustained high temperatures from overloading or external heat sources.",
        importance: "Thermal aging is cumulative and irreversible. Each temperature excursion above rated limits accelerates degradation exponentially.",
        approach: "Review loading history and thermal profile. Perform insulation resistance trending. Consider derating if thermal limits exceeded.",
        severity: "Moderate",
        relatedTests: ["Insulation Resistance Test", "VLF Withstand Test"],
        relatedMonitoring: ["Distributed Temperature Sensing", "Load Monitoring"],
        images: []
      },
      {
        name: "Electrical Treeing",
        description: "Branch-like carbonized channels that propagate through insulation due to sustained partial discharge activity.",
        importance: "Electrical trees indicate imminent failure. Once formed, they propagate rapidly under normal operating voltage.",
        approach: "Immediate assessment required. If electrical treeing confirmed, plan cable replacement as failure is likely.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test", "VLF Tan Delta Test"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      },
      {
        name: "Insulation Contamination",
        description: "Introduction of foreign materials (moisture, chemicals, particles) into insulation during manufacturing or through damage.",
        importance: "Contaminants create weak points where partial discharge initiates. Manufacturing defects may not appear until years later.",
        approach: "Perform factory acceptance testing on new cables. Investigate any PD hot spots for contamination sources.",
        severity: "Moderate",
        relatedTests: ["Partial Discharge Test", "VLF Withstand Test"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      }
    ],
    tests: [
      {
        name: "VLF Tan Delta Test",
        description: "Measures dielectric losses in cable insulation using Very Low Frequency (0.1 Hz or 0.01 Hz) test voltage.",
        importance: "Highly effective at detecting water tree degradation in XLPE cables. Provides condition assessment without damaging insulation.",
        approach: "Apply VLF voltage and measure tan delta at multiple voltage levels. Analyze tip-up characteristics for water tree indication.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Water Treeing", "Thermal Degradation", "Partial Discharge"],
        images: []
      },
      {
        name: "Partial Discharge Test",
        description: "Detects and locates partial discharge activity within cable insulation using electrical or acoustic sensors.",
        importance: "PD is a precursor to cable failure. Early detection allows planned replacement before in-service failure.",
        approach: "Apply test voltage and monitor for PD pulses. Use Time Domain Reflectometry (TDR) to locate PD sites along cable length.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Partial Discharge", "Electrical Treeing", "Insulation Contamination"],
        images: []
      },
      {
        name: "VLF Withstand Test",
        description: "Applies elevated VLF voltage for specified duration to verify cable can withstand operating stresses.",
        importance: "Proves cable integrity after installation or repair. Identifies weak points before energization.",
        approach: "Apply 2-3x rated voltage at VLF for 15-60 minutes. Monitor for breakdown or excessive leakage current.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Insulation Contamination", "Thermal Degradation"],
        images: []
      },
      {
        name: "Insulation Resistance Test",
        description: "Measures DC resistance of cable insulation to ground, indicating overall insulation condition.",
        importance: "Simple first-line test to identify gross insulation problems. Useful for trending over time.",
        approach: "Apply DC voltage (typically 5kV for MV cables) and measure insulation resistance. Compare to minimum acceptable values.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Thermal Degradation", "Water Treeing"],
        images: []
      },
      {
        name: "Dielectric Spectroscopy",
        description: "Advanced technique measuring insulation response across a range of frequencies to characterize aging mechanisms.",
        importance: "Provides detailed insight into insulation condition including moisture content and aging state.",
        approach: "Apply test signals at multiple frequencies. Analyze frequency response patterns to identify degradation mechanisms.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Water Treeing", "Thermal Degradation"],
        images: []
      }
    ],
    monitoring: [
      {
        name: "Online PD Monitoring",
        description: "Continuous monitoring for partial discharge activity using sensors installed at cable terminations or joints.",
        importance: "Provides early warning of developing insulation problems. Enables condition-based maintenance approach.",
        approach: "Install HFCT sensors at accessible points. Connect to monitoring system for trending and alarming.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Partial Discharge", "Electrical Treeing"],
        images: []
      },
      {
        name: "Distributed Temperature Sensing",
        description: "Fiber optic sensing along entire cable length providing real-time temperature profile.",
        importance: "Identifies hot spots from overloading, external heat sources, or degraded insulation before failure.",
        approach: "Install fiber optic cable alongside power cable. Monitor for temperature anomalies and trending.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Thermal Degradation", "Water Treeing"],
        images: []
      },
      {
        name: "Load Monitoring",
        description: "Continuous monitoring of cable current loading and calculation of cumulative thermal stress.",
        importance: "Prevents overloading damage and enables dynamic rating based on actual thermal conditions.",
        approach: "Install current transformers and temperature sensors. Calculate real-time ampacity limits.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Thermal Degradation"],
        images: []
      }
    ]
  },
  {
    id: "2",
    number: 2,
    name: "Cable Terminations",
    color: "#EC4899",
    failureModes: [
      {
        name: "Stress Cone Failure",
        description: "Degradation or misalignment of the stress relief cone that controls electric field distribution at cable end.",
        importance: "Stress cone failure leads to concentrated electric stress, causing PD and eventual flashover.",
        approach: "Inspect termination geometry. Perform PD testing focused on termination. Replace if stress cone damaged.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test", "Infrared Thermography"],
        relatedMonitoring: ["Online PD Monitoring", "Temperature Monitoring"],
        images: []
      },
      {
        name: "Moisture Ingress",
        description: "Water penetration into termination through damaged seals, cracks, or improper installation.",
        importance: "Moisture dramatically reduces insulation strength and initiates tracking and PD activity.",
        approach: "Inspect seals and housing integrity. Check for signs of tracking or contamination. Reseal or replace as needed.",
        severity: "Critical",
        relatedTests: ["Insulation Resistance Test", "Visual Inspection"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      },
      {
        name: "Tracking",
        description: "Formation of conductive carbon paths on insulation surface due to contamination and moisture.",
        importance: "Tracking progressively shortens creepage distance, leading to flashover under wet or contaminated conditions.",
        approach: "Clean termination surfaces. Apply hydrophobic coating. Replace severely tracked components.",
        severity: "Moderate",
        relatedTests: ["Visual Inspection", "Insulation Resistance Test"],
        relatedMonitoring: ["Temperature Monitoring"],
        images: []
      },
      {
        name: "Connection Overheating",
        description: "Excessive temperature at conductor connection due to loose bolts, corrosion, or undersized lugs.",
        importance: "Hot connections accelerate oxidation, increasing resistance in a thermal runaway cycle.",
        approach: "Perform infrared survey under load. Retorque connections. Clean and apply contact compound.",
        severity: "Moderate",
        relatedTests: ["Infrared Thermography", "Connection Resistance Test"],
        relatedMonitoring: ["Temperature Monitoring"],
        images: []
      },
      {
        name: "Insulator Contamination",
        description: "Accumulation of pollution, salt, or industrial contaminants on external insulator surfaces.",
        importance: "Contamination reduces flashover voltage, especially under humid or wet conditions.",
        approach: "Regular cleaning schedule. Apply RTV silicone coating. Consider pollution-resistant designs.",
        severity: "Moderate",
        relatedTests: ["Visual Inspection", "ESDD Measurement"],
        relatedMonitoring: ["Leakage Current Monitoring"],
        images: []
      }
    ],
    tests: [
      {
        name: "Infrared Thermography",
        description: "Non-contact temperature measurement of termination components using thermal imaging camera.",
        importance: "Identifies hot connections and overheating components before failure. Quick and non-invasive.",
        approach: "Scan terminations under load conditions. Compare phase-to-phase temperatures. Investigate anomalies.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Connection Overheating", "Stress Cone Failure"],
        images: []
      },
      {
        name: "Connection Resistance Test",
        description: "Measures resistance of bolted connections to identify high-resistance joints.",
        importance: "Detects loose or corroded connections that cause overheating.",
        approach: "Use micro-ohmmeter to measure connection resistance. Compare to manufacturer specifications.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Connection Overheating"],
        images: []
      },
      {
        name: "Visual Inspection",
        description: "Detailed visual examination of termination condition including housing, seals, and insulators.",
        importance: "Identifies obvious defects like cracks, tracking marks, oil leaks, and corrosion.",
        approach: "Systematic inspection checklist. Document with photographs. UV inspection for corona activity.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Tracking", "Moisture Ingress", "Insulator Contamination"],
        images: []
      },
      {
        name: "Partial Discharge Test",
        description: "PD measurement focused on termination using portable or online sensors.",
        importance: "Detects PD activity in stress cone area indicating developing failure.",
        approach: "Apply HFCT sensor at termination. Monitor PD levels and patterns during voltage application.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Stress Cone Failure", "Moisture Ingress"],
        images: []
      }
    ],
    monitoring: [
      {
        name: "Online PD Monitoring",
        description: "Continuous PD monitoring at terminations using permanently installed sensors.",
        importance: "Early warning of termination problems. Enables planned replacement before failure.",
        approach: "Install HFCT sensors at each termination. Connect to central monitoring system.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Stress Cone Failure", "Moisture Ingress"],
        images: []
      },
      {
        name: "Temperature Monitoring",
        description: "Continuous temperature monitoring of termination connections and housing.",
        importance: "Detects overheating from connection problems or overloading.",
        approach: "Install RTD or fiber optic sensors. Set alarm thresholds based on rating.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Connection Overheating", "Stress Cone Failure"],
        images: []
      },
      {
        name: "Leakage Current Monitoring",
        description: "Monitors surface leakage current on outdoor termination insulators.",
        importance: "Indicates contamination level and flashover risk under adverse conditions.",
        approach: "Install leakage current sensors. Correlate with weather data for predictive maintenance.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Insulator Contamination", "Tracking"],
        images: []
      }
    ]
  },
  {
    id: "3",
    number: 3,
    name: "Cable Joints/Splices",
    color: "#F59E0B",
    failureModes: [
      {
        name: "Joint Void Formation",
        description: "Air pockets or voids within joint insulation due to improper installation or material shrinkage.",
        importance: "Voids are sites of partial discharge initiation. PD erodes surrounding insulation leading to failure.",
        approach: "Perform PD testing on joints. If significant PD detected, plan joint replacement.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test", "VLF Tan Delta Test"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      },
      {
        name: "Connector Failure",
        description: "Mechanical or electrical failure of the conductor connector within the joint.",
        importance: "Connector failure causes high resistance, overheating, and eventual open circuit or fire.",
        approach: "Monitor joint temperature during operation. Investigate any temperature anomalies immediately.",
        severity: "Critical",
        relatedTests: ["Infrared Thermography", "DC Resistance Test"],
        relatedMonitoring: ["Distributed Temperature Sensing", "Online PD Monitoring"],
        images: []
      },
      {
        name: "Insulation Interface Breakdown",
        description: "Failure at the interface between cable insulation and joint insulation materials.",
        importance: "Interface defects create field enhancements where PD initiates. Critical installation quality issue.",
        approach: "Ensure proper surface preparation during installation. Perform post-installation PD testing.",
        severity: "Critical",
        relatedTests: ["Partial Discharge Test", "VLF Withstand Test"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      },
      {
        name: "Thermal Cycling Damage",
        description: "Mechanical stress from repeated expansion and contraction cycles causing insulation damage.",
        importance: "Daily load cycling creates cumulative mechanical stress at interfaces and connections.",
        approach: "Design joints for expected thermal cycling. Monitor for PD development over time.",
        severity: "Moderate",
        relatedTests: ["Partial Discharge Test", "VLF Tan Delta Test"],
        relatedMonitoring: ["Distributed Temperature Sensing"],
        images: []
      },
      {
        name: "Shield Interruption",
        description: "Improper reconnection of cable metallic shield/screen across the joint.",
        importance: "Shield interruption causes localized voltage stress and circulating currents.",
        approach: "Verify shield continuity during installation. Test shield resistance end-to-end.",
        severity: "Moderate",
        relatedTests: ["Shield Continuity Test", "Partial Discharge Test"],
        relatedMonitoring: ["Online PD Monitoring"],
        images: []
      }
    ],
    tests: [
      {
        name: "Partial Discharge Test",
        description: "PD testing with TDR location to identify and locate discharge activity within joints.",
        importance: "Joints are statistically the most likely point of cable failure. PD testing is essential.",
        approach: "Apply test voltage and monitor PD. Use TDR to confirm PD is located at joint position.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Joint Void Formation", "Insulation Interface Breakdown", "Shield Interruption"],
        images: []
      },
      {
        name: "Infrared Thermography",
        description: "Thermal imaging of accessible joint locations to identify overheating.",
        importance: "Detects connector problems and abnormal heating from increased resistance.",
        approach: "Survey joints under load conditions. Note that buried joints require DTS instead.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Connector Failure"],
        images: []
      },
      {
        name: "DC Resistance Test",
        description: "Measures end-to-end conductor resistance to identify high-resistance joints.",
        importance: "High resistance indicates connector problems or conductor damage.",
        approach: "Measure resistance and compare to calculated value based on conductor size and length.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Connector Failure"],
        images: []
      },
      {
        name: "Shield Continuity Test",
        description: "Verifies proper reconnection of metallic shield across all joints.",
        importance: "Ensures shield provides proper fault current path and EMI shielding.",
        approach: "Measure shield resistance end-to-end. Should be consistent with shield cross-section.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Shield Interruption"],
        images: []
      },
      {
        name: "VLF Tan Delta Test",
        description: "Dielectric loss measurement to assess overall joint insulation condition.",
        importance: "Detects moisture or contamination within joint insulation.",
        approach: "Include joints in cable tan delta testing. Look for abnormal results.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Joint Void Formation", "Thermal Cycling Damage"],
        images: []
      }
    ],
    monitoring: [
      {
        name: "Online PD Monitoring",
        description: "Continuous PD monitoring with sensors positioned to detect joint activity.",
        importance: "Joints are failure hot spots - continuous monitoring essential for critical circuits.",
        approach: "Install sensors to cover joint locations. Configure alarms for PD trending.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Joint Void Formation", "Insulation Interface Breakdown"],
        images: []
      },
      {
        name: "Distributed Temperature Sensing",
        description: "Fiber optic temperature monitoring covering all joint locations along cable route.",
        importance: "Only practical method to monitor temperature of buried or inaccessible joints.",
        approach: "Ensure DTS fiber passes through or adjacent to each joint location.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Connector Failure", "Thermal Cycling Damage"],
        images: []
      }
    ]
  },
  {
    id: "4",
    number: 4,
    name: "Cable Sheath & Armor",
    color: "#10B981",
    failureModes: [
      {
        name: "Sheath Damage",
        description: "Physical damage to outer protective sheath from installation, dig-ins, or rodent attack.",
        importance: "Damaged sheath allows moisture ingress which degrades insulation over time.",
        approach: "Perform sheath integrity test to locate faults. Repair or replace damaged sections.",
        severity: "Moderate",
        relatedTests: ["Sheath Integrity Test", "Visual Inspection"],
        relatedMonitoring: ["Distributed Acoustic Sensing"],
        images: []
      },
      {
        name: "Armor Corrosion",
        description: "Electrochemical corrosion of steel wire or tape armor, especially in aggressive soil conditions.",
        importance: "Corroded armor loses mechanical protection capability and may affect grounding.",
        approach: "Inspect exposed armor sections. Measure armor resistance to assess corrosion extent.",
        severity: "Moderate",
        relatedTests: ["Visual Inspection", "Armor Continuity Test"],
        relatedMonitoring: ["Cathodic Protection Monitoring"],
        images: []
      },
      {
        name: "Water Penetration",
        description: "Longitudinal water migration under sheath following sheath breach.",
        importance: "Water can travel long distances under sheath, affecting cable far from damage point.",
        approach: "Use water-blocked cable designs. Locate and repair sheath damage promptly.",
        severity: "Critical",
        relatedTests: ["Sheath Integrity Test", "Moisture Analysis"],
        relatedMonitoring: ["Distributed Moisture Sensing"],
        images: []
      },
      {
        name: "Thermomechanical Damage",
        description: "Sheath cracking or splitting due to cable movement from thermal expansion cycles.",
        importance: "Common at cable bends and where cable is constrained. Creates moisture entry points.",
        approach: "Ensure adequate expansion provisions at design stage. Inspect high-stress points.",
        severity: "Moderate",
        relatedTests: ["Visual Inspection", "Sheath Integrity Test"],
        relatedMonitoring: ["Distributed Temperature Sensing"],
        images: []
      }
    ],
    tests: [
      {
        name: "Sheath Integrity Test",
        description: "DC voltage test between metallic sheath/armor and earth to detect sheath damage.",
        importance: "Locates sheath breaches that allow water ingress. Should be performed after installation.",
        approach: "Apply DC voltage (typically 5-10kV) to sheath. Monitor for leakage or breakdown indicating damage.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Sheath Damage", "Water Penetration", "Thermomechanical Damage"],
        images: []
      },
      {
        name: "Armor Continuity Test",
        description: "Measures end-to-end resistance of cable armor to verify integrity and grounding.",
        importance: "Confirms armor provides proper fault current path and mechanical protection.",
        approach: "Measure resistance and compare to expected value. High resistance indicates corrosion or breaks.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Armor Corrosion"],
        images: []
      },
      {
        name: "Visual Inspection",
        description: "Physical examination of accessible cable sections for damage, corrosion, or degradation.",
        importance: "Simple but effective method to identify obvious problems.",
        approach: "Inspect at manholes, terminations, and any accessible points. Document with photographs.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Sheath Damage", "Armor Corrosion", "Thermomechanical Damage"],
        images: []
      }
    ],
    monitoring: [
      {
        name: "Distributed Acoustic Sensing",
        description: "Fiber optic sensing to detect third-party interference and mechanical disturbance.",
        importance: "Real-time detection of dig-ins and unauthorized activity near cable route.",
        approach: "Install fiber optic cable along route. Monitor for acoustic signatures of digging.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Sheath Damage"],
        images: []
      },
      {
        name: "Cathodic Protection Monitoring",
        description: "Monitoring of cathodic protection system effectiveness for steel-armored cables.",
        importance: "Ensures armor corrosion protection is functioning properly.",
        approach: "Monitor protection potential at test points. Adjust rectifier output as needed.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Armor Corrosion"],
        images: []
      }
    ]
  },
  {
    id: "5",
    number: 5,
    name: "Conductor",
    color: "#EF4444",
    failureModes: [
      {
        name: "Conductor Overheating",
        description: "Excessive temperature from sustained overloading beyond cable ampacity rating.",
        importance: "Overheating accelerates insulation aging and can cause conductor annealing.",
        approach: "Review loading history. Implement load management or cable upgrade.",
        severity: "Moderate",
        relatedTests: ["DC Resistance Test", "Infrared Thermography"],
        relatedMonitoring: ["Load Monitoring", "Distributed Temperature Sensing"],
        images: []
      },
      {
        name: "Conductor Corrosion",
        description: "Chemical attack on conductor material, especially aluminum in presence of moisture.",
        importance: "Corrosion increases resistance and reduces current-carrying capacity.",
        approach: "Inspect accessible conductor points. Ensure proper sealing at terminations.",
        severity: "Moderate",
        relatedTests: ["DC Resistance Test", "Visual Inspection"],
        relatedMonitoring: ["Temperature Monitoring"],
        images: []
      },
      {
        name: "Conductor Fatigue",
        description: "Work hardening and eventual fracture of conductor strands from repeated bending or vibration.",
        importance: "Progressive strand breakage reduces ampacity and eventually causes open circuit.",
        approach: "Avoid repeated bending during installation. Investigate any resistance increase.",
        severity: "Critical",
        relatedTests: ["DC Resistance Test"],
        relatedMonitoring: ["Load Monitoring"],
        images: []
      },
      {
        name: "Hot Spot at Shield Gap",
        description: "Localized heating due to induced currents at points where metallic shield is interrupted.",
        importance: "Can cause significant local heating and accelerated insulation degradation.",
        approach: "Ensure proper shield bonding design. Monitor temperature at shield breaks.",
        severity: "Moderate",
        relatedTests: ["Infrared Thermography", "Shield Bonding Test"],
        relatedMonitoring: ["Distributed Temperature Sensing"],
        images: []
      }
    ],
    tests: [
      {
        name: "DC Resistance Test",
        description: "Measures conductor resistance to identify degradation or damage.",
        importance: "Baseline measurement for trending. Resistance increase indicates problems.",
        approach: "Measure at controlled temperature. Compare to calculated value and previous measurements.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Conductor Corrosion", "Conductor Fatigue", "Conductor Overheating"],
        images: []
      },
      {
        name: "Infrared Thermography",
        description: "Thermal imaging of accessible conductor connections and terminations.",
        importance: "Identifies hot spots from high-resistance connections or overloading.",
        approach: "Survey under normal load conditions. Investigate any temperature anomalies.",
        complexity: "Simple",
        cost: "$",
        detectsFailures: ["Conductor Overheating", "Hot Spot at Shield Gap"],
        images: []
      },
      {
        name: "Shield Bonding Test",
        description: "Verifies proper configuration of shield bonding to minimize circulating currents.",
        importance: "Improper bonding can cause overheating and reduce ampacity.",
        approach: "Measure shield voltages and currents. Verify bonding matches design intent.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Hot Spot at Shield Gap"],
        images: []
      }
    ],
    monitoring: [
      {
        name: "Load Monitoring",
        description: "Continuous monitoring of cable loading current and temperature.",
        importance: "Prevents overloading damage and enables dynamic cable rating.",
        approach: "Install CTs and temperature sensors. Calculate real-time ampacity.",
        complexity: "Standard",
        cost: "$$",
        detectsFailures: ["Conductor Overheating", "Conductor Fatigue"],
        images: []
      },
      {
        name: "Distributed Temperature Sensing",
        description: "Fiber optic temperature profile along entire cable length.",
        importance: "Identifies hot spots anywhere along cable including inaccessible locations.",
        approach: "Install fiber alongside cable. Monitor for temperature anomalies.",
        complexity: "Advanced",
        cost: "$$$",
        detectsFailures: ["Conductor Overheating", "Hot Spot at Shield Gap"],
        images: []
      }
    ]
  }
];

// Simple cable component list for overview
export const getSimpleCableComponents = () => {
  return CABLE_DATA.map(comp => ({
    id: comp.id,
    name: comp.name,
    color: comp.color
  }));
};

// Get risk level badge styling
export const getCableRiskBadge = (level) => {
  const badges = {
    low: { bg: "bg-green-100", text: "text-green-800", label: "LOW" },
    medium: { bg: "bg-yellow-100", text: "text-yellow-800", label: "MEDIUM" },
    high: { bg: "bg-orange-100", text: "text-orange-800", label: "HIGH" },
    critical: { bg: "bg-red-100", text: "text-red-800", label: "CRITICAL" }
  };
  return badges[level] || badges.medium;
};

export default CABLE_DATA;
